import { useCallback, useEffect } from 'react';
import { Button, PhoneInput, isValidPhoneNumber } from '@restworld/ui-ds';
import { Controller } from 'react-hook-form';
import tw from 'twin.macro';
import { ModalHeader, ModalSubHeader } from '../common';
import { useVerifyPhoneNumberForm } from '../../hooks/use-verify-phone-number-form';
import { useAtom, useSetAtom } from 'jotai';
import { initializePhoneNumberValidation, setIsValidateOtp } from './states';
import { useAccountsService } from '@restworld/data-services';
import { FlexCol } from '../header';
import { useTranslation } from 'next-i18next';
import { useAnalytics } from '@restworld/utils-common';

export function useEnterPhoneNumberContent({ phoneNumber }: { phoneNumber: string }) {
  const { loading, onSubmit, handleSubmit, control, errors } = useVerifyPhoneNumberForm({
    phoneNumber,
  });
  const { trackEvent } = useAnalytics();
  const [verifyPhoneNumber, setVerifyPhoneNumber] = useAtom(initializePhoneNumberValidation);
  const setValidateOtpContent = useSetAtom(setIsValidateOtp);
  const acc = useAccountsService();
  const { t } = useTranslation('user-settings');

  const handleVerification = useCallback(async () => {
    try {
      trackEvent('worker_request_otp_for_phone_number_change', { phone_number: phoneNumber });
      await acc.sendVerificationPhoneOtp();
      setVerifyPhoneNumber(false);
      setValidateOtpContent(true);
    } catch (error) {
      setVerifyPhoneNumber(false);
    }
  }, [acc, phoneNumber, setValidateOtpContent, setVerifyPhoneNumber, trackEvent]);

  useEffect(() => {
    if (verifyPhoneNumber) {
      handleVerification();
    }
  }, [handleVerification, verifyPhoneNumber]);

  const enterPhoneNumberContent = (
    <div tw="flex flex-col items-center">
      <FlexCol tw="items-center gap-2 justify-start mb-4">
        <ModalHeader>{t('enter_phone_number_content_header')}</ModalHeader>
        <ModalSubHeader>{t('enter_phone_number_content_subheader')}</ModalSubHeader>
      </FlexCol>
      <form tw="flex flex-col gap-8 w-full">
        <Controller
          name="phoneNumber"
          control={control}
          rules={{ required: true, validate: isValidPhoneNumber }}
          render={({ field }) => (
            <PhoneInput
              label={
                <span>
                  {t('account_tab_phone_number_field')}
                  <span tw="text-red-600">*</span>
                </span>
              }
              onChange={field.onChange}
              priorityCountries={['IT']}
              menuDialogStyles={[tw`min-w-full`]}
              variant={errors['phoneNumber'] ? 'error' : 'primary'}
              supportText={errors['phoneNumber']?.message as string}
              value={field.value}
            />
          )}
        />
      </form>
    </div>
  );

  const enterPhoneNumberFooter = (
    <Button onClick={handleSubmit(onSubmit)} fullwidth={true} loading={loading || verifyPhoneNumber}>
      {t('enter_phone_number_content_action_button_text')}
    </Button>
  );

  return {
    enterPhoneNumberContent,
    enterPhoneNumberFooter,
  };
}
